import type { CountryCode } from 'libphonenumber-js';
import { IContact } from '../../../types/contactModule';
import { formatWhen } from '../../../../utils/sms';
import { parseFlexiblePhoneNumber } from '../../../../utils/utils';

/**
 * One row of the contact list, worked out once when a page arrives so the
 * template only reads fields.
 */
export interface ContactRow {
  /** The API record, handed back to the table's existing handlers as-is. */
  contact: IContact;
  /** Stable `@for` key: the uuid, else the id, else the row's position. */
  key: string;
  name: string;
  /** Email under the name, or null when there is none. */
  email: string | null;
  /** International format when the number parses, else as stored. */
  phone: string;
  /** Why this contact cannot receive SMS, or '' when the number reads fine. */
  phoneIssue: '' | 'Invalid number' | 'No number';
  organizations: string;
  organizationsTitle: string | null;
  tags: string;
  tagsTitle: string | null;
  source: string;
  added: string;
  addedTitle: string | null;
}

/** "Acme", "Acme +2" — the first name and how many more. */
function firstPlus(names: string[]): string {
  if (!names.length) return '—';
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
}

/** Words that stay in capitals when a source is turned into a label. */
const ACRONYMS = new Set(['csv', 'api', 'ussd', 'sms', 'otp', 'url', 'crm']);

/** "CSV_UPLOAD" → "CSV upload", "API" → "API", "WEB_FORM" → "Web form". */
export function humanizeSource(value: string | null | undefined): string {
  const words = (value ?? '').toString().replace(/[_-]+/g, ' ').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  return words
    .map((word, i) => (ACRONYMS.has(word) ? word.toUpperCase() : i === 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

export function contactName(contact: IContact | null | undefined): string {
  if (!contact) return '';
  const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  return name || (contact.fullName ?? '').trim();
}

/**
 * Reads a stored number the way the import does: as written when it starts
 * with "+", else in the contact's region, else with its saved dial code.
 */
export function readPhone(contact: IContact | null | undefined) {
  const raw = (contact?.primaryContactNumber ?? '').toString().trim();
  if (!raw) return undefined;
  const region = (contact?.countryCode ?? '').toString().toUpperCase() || undefined;
  let parsed = parseFlexiblePhoneNumber(raw, region as CountryCode | undefined);
  if (!parsed && !raw.startsWith('+')) {
    const dial = (contact?.zipCode ?? '').toString().replace(/\D/g, '');
    const digits = raw.replace(/\D/g, '').replace(/^0+/, '');
    if (dial && digits && dial.length <= 4) {
      parsed = parseFlexiblePhoneNumber(`+${dial}${digits}`);
    }
  }
  return parsed;
}

export function toContactRows(contacts: unknown): ContactRow[] {
  if (!Array.isArray(contacts)) return [];

  return contacts
    .filter((contact) => !!contact && typeof contact === 'object')
    .map((contact: IContact, index): ContactRow => {
      const raw = (contact.primaryContactNumber ?? '').toString().trim();
      const parsed = readPhone(contact);
      const orgNames = (contact.companyEntities ?? []).map((org) => org?.name).filter(Boolean) as string[];
      const tagNames = (contact.entityTags ?? []).map((tag) => tag?.name).filter(Boolean) as string[];
      const email = (contact.emailAddress ?? '').toString().trim();
      const key = contact.uuid ? String(contact.uuid) : contact.id != null ? `id-${contact.id}` : `row-${index}`;

      return {
        contact,
        key,
        name: contactName(contact) || 'No name',
        email: email || null,
        phone: parsed ? parsed.formatInternational() : raw || '—',
        phoneIssue: !raw ? 'No number' : parsed ? '' : 'Invalid number',
        organizations: firstPlus(orgNames),
        organizationsTitle: orgNames.length > 1 ? orgNames.join(', ') : null,
        tags: firstPlus(tagNames),
        tagsTitle: tagNames.length > 1 ? tagNames.join(', ') : null,
        source: humanizeSource(contact.origin),
        added: formatWhen(contact.createdAt),
        addedTitle: contact.createdAt ? new Date(contact.createdAt).toLocaleString('en-GB') : null,
      };
    });
}
